import { useEffect, useMemo, useState } from "react"
import { fmtCompact, toNum } from "../game/format"
import { useBakeryStore } from "../game/bakeryStore"
import { sfxFanfare } from "../game/music"
import type { SnapshotProject } from "../game/types"

function fmtLeft(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "finishing…"
  const s = Math.ceil(ms / 1000)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  if (h > 0) return `${h}h ${m}m left`
  if (m > 0) return `${m}m ${String(sec).padStart(2, "0")}s left`
  return `${sec}s left`
}

function livePct(p: SnapshotProject, now: number): number {
  if (typeof p.progressPct === "number" && Number.isFinite(p.progressPct) && p.progressPct > 0) {
    if (typeof p.endsAt === "number" && typeof p.startedAt === "number" && p.endsAt > p.startedAt) {
      const dur = p.endsAt - p.startedAt
      const el = Math.max(0, Math.min(dur, now - p.startedAt))
      return Math.max(0, Math.min(100, (el / dur) * 100))
    }
    return Math.max(0, Math.min(100, p.progressPct))
  }
  if (typeof p.endsAt === "number" && typeof p.startedAt === "number" && p.endsAt > p.startedAt) {
    const dur = p.endsAt - p.startedAt
    const el = Math.max(0, Math.min(dur, now - p.startedAt))
    return Math.max(0, Math.min(100, (el / dur) * 100))
  }
  const cost = toNum(p.cookieCost)
  const prog = toNum(p.progress)
  if (cost > 0) return Math.max(0, Math.min(100, (prog / cost) * 100))
  return 0
}

function liveLeft(p: SnapshotProject, now: number): number {
  if (typeof p.endsAt === "number" && Number.isFinite(p.endsAt)) return Math.max(0, p.endsAt - now)
  return Math.max(0, p.timeLeftMs ?? 0)
}

export default function ActiveProjectCard({ compact }: { compact?: boolean }) {
  const store = useBakeryStore()
  const snap = store.snapshot
  const [now, setNow] = useState(0)
  useEffect(() => {
    setNow(Date.now());
  }, []);
  const [fanfare, setFanfare] = useState<string | null>(null)

  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(t)
  }, [])

  useEffect(() => {
    const off = store.subscribe((e) => {
      if (e.type === "project_completed") {
        const pid = (e as unknown as { projectId?: string }).projectId ?? ""
        const name = snap?.projects?.find((p) => p.id === pid)?.name ?? "Project complete!"
        setFanfare(name)
        try {
          sfxFanfare()
        } catch {
          return
        }
        window.setTimeout(() => setFanfare(null), 4200)
      }
      if (e.type === "project_started") {
        setNow(Date.now())
      }
    })
    return off
  }, [store, snap])

  const { active, next } = useMemo(() => {
    const projects = snap?.projects ?? []
    const a = projects.find((p) => p.status === "in_progress") ?? null
    const done = new Set(projects.filter((p) => p.status === "completed").map((p) => p.id))
    const cand = projects
      .filter((p) => p.status !== "completed" && p.status !== "in_progress")
      .filter((p) => {
        if ((p.cooldownRemainingMs ?? 0) > 0) return false
        if (p.exclusiveGroup && projects.some((q) => q.id !== p.id && q.exclusiveGroup === p.exclusiveGroup && q.status === "completed")) return false
        return true
      })
      .sort((x, y) => {
        const xd = x.requiresProjectId && !done.has(x.requiresProjectId) ? 1 : 0
        const yd = y.requiresProjectId && !done.has(y.requiresProjectId) ? 1 : 0
        if (xd !== yd) return xd - yd
        return (x.requiredBakeryLevel ?? 1) - (y.requiredBakeryLevel ?? 1)
      })
    return { active: a, next: cand[0] ?? null }
  }, [snap])

  if (snap === null) {
    return (
      <div className="cb-side-card" role="status" aria-live="polite">
        <small className="dim">Next Project</small>
        <div><b>Loading…</b></div>
      </div>
    )
  }

  const cap = (s: string): string => (s === "" ? s : s.charAt(0).toUpperCase() + s.slice(1))

  return (
    <div className="cb-side-card cb-active-project" role="status" aria-live="polite">
      {fanfare !== null && (
        <div className="cb-fanfare" role="alert">
          <div className="cb-fanfare-pop">🎉 {fanfare} complete! 🎉</div>
          <div className="cb-fanfare-sub">Bakery CPS up — keep baking!</div>
        </div>
      )}
      {active !== null ? (
        <>
          <div className="cb-construct-head">
            <span className="cb-crane" aria-hidden="true">🏗</span>
            <span>
              <small className="dim">Under construction</small>
              <div><b>{active.name}</b></div>
            </span>
            <span className="cb-pill">{fmtLeft(liveLeft(active, now))}</span>
          </div>
          <div className="cb-track cb-construct" role="progressbar" aria-valuenow={Math.round(livePct(active, now))} aria-valuemin={0} aria-valuemax={100} aria-label={`${active.name} progress`}>
            <div className="cb-fill-gold" style={{ width: `${livePct(active, now)}%` }} />
            <span className="cb-construct-emoji" aria-hidden="true">🚧🧱🚧</span>
          </div>
          <small className="dim">{Math.floor(livePct(active, now))}% · +{fmtCompact(active.cpsBonus)}/sec on completion · Ends {typeof active.endsAt === "number" ? new Date(active.endsAt).toLocaleTimeString() : "soon"}</small>
          {/* No spec badges for an in-progress build: specs were met at
              start and presence may legitimately drop mid-build. Showing
              ✕ missing here would overwarn for an untrue state. */}
          {(active.contributors ?? []).length > 0 && (
            <div className="cb-contrib">
              <small className="dim">Top contributors</small>
              {(active.contributors ?? []).slice(0, 5).map((c, i) => (
                <div key={c.userId} className="cb-contrib-row">
                  <span aria-hidden="true">{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : "🍪"}</span>
                  <b>{c.name}</b>
                  <span className="cb-num">{fmtCompact(c.amount)}</span>
                </div>
              ))}
            </div>
          )}
          {!compact && next !== null && (
            <div className="cb-next">
              <small className="dim">Next unlock: <b>{next.name}</b> · 🍪 {fmtCompact(next.cookieCost)} · Lv {next.requiredBakeryLevel ?? 1}+</small>
            </div>
          )}
        </>
      ) : (
        <>
          <small className="dim">Next Project</small>
          {next !== null ? (
            <>
              <div><b>🔓 {next.name}</b></div>
              <small className="dim">{next.description}</small>
              <small className="dim">🍪 {fmtCompact(next.cookieCost)} + 🪙 {fmtCompact(next.personalCost ?? String(Math.max(1, Math.floor(toNum(next.cookieCost) / 10))))} personal · +{fmtCompact(next.cpsBonus)}/sec · Lv {next.requiredBakeryLevel ?? 1}+{(next.requiredSpecs ?? []).length > 0 ? ` · Needs ${(next.requiredSpecs ?? []).map(cap).join(" + ")} together (all active at once)` : ""}</small>
            </>
          ) : (
            <>
              <div><b>No active project</b></div>
              <small className="dim">Projects appear here once the bakery starts one.</small>
            </>
          )}
        </>
      )}
    </div>
  )
}
